/**
 * PaymentPlanManager - Manage payment plans and installments for delegates
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, DollarSign, Calendar, CreditCard, CheckCircle, AlertCircle,
  Loader2, Clock, Receipt, User, TrendingUp, Percent
} from 'lucide-react';
import api from '@/lib/api';

const PaymentPlanManager = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityType, setEntityType] = useState('delegate'); // delegate or sponsor
  const [statusFilter, setStatusFilter] = useState('all');
  const [manualEntry, setManualEntry] = useState(false);
  const [manualName, setManualName] = useState('');
  
  const [planForm, setPlanForm] = useState({
    total_amount: '',
    initial_payment: '',
    installment_count: 2,
    processing_fee_type: 'none',
    processing_fee_amount: 0,
    notes: ''
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: ''
  });

  // Fetch payment plans
  const { data: plans = [], isLoading: loadingPlans } = useQuery({
    queryKey: ['payment-plans', eventId, statusFilter],
    queryFn: () => api.get(`/admin/events/${eventId}/payment-plans`, {
      params: statusFilter !== 'all' ? { status: statusFilter } : {}
    }).then(r => r.data),
  });

  // Fetch delegates for creating new plans
  const { data: delegates = [] } = useQuery({
    queryKey: ['delegates-for-plans', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/delegates`, {
      params: { limit: 500 }
    }).then(r => r.data?.delegates || r.data || []),
  });

  // Fetch sponsors for creating new plans
  const { data: sponsors = [] } = useQuery({
    queryKey: ['sponsors-for-plans', eventId],
    queryFn: () => api.get(`/admin/events/${eventId}/sponsors`).then(r => r.data || []),
  });

  // Create payment plan using new unified endpoint
  const createPlanMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/payment-plans`, data),
    onSuccess: () => {
      toast.success('Payment plan created successfully');
      queryClient.invalidateQueries(['payment-plans', eventId]);
      setShowCreateDialog(false);
      resetPlanForm();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create payment plan')
  });

  // Record payment using new unified endpoint
  const recordPaymentMutation = useMutation({
    mutationFn: ({ planId, data }) => 
      api.post(`/admin/events/${eventId}/payment-plans/${planId}/payment`, data),
    onSuccess: (response) => {
      toast.success(`Payment of $${paymentForm.amount} recorded. Balance: $${response.data.balance.toFixed(2)}`);
      queryClient.invalidateQueries(['payment-plans', eventId]);
      setShowPaymentDialog(false);
      resetPaymentForm();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to record payment')
  });

  const resetPlanForm = () => {
    setPlanForm({
      total_amount: '',
      initial_payment: '',
      installment_count: 2,
      processing_fee_type: 'none',
      processing_fee_amount: 0,
      notes: ''
    });
    setSelectedEntity(null);
    setEntityType('delegate');
    setManualEntry(false);
    setManualName('');
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
    setSelectedPlan(null);
  };

  const handleCreatePlan = (e) => {
    e.preventDefault();
    
    if (!manualEntry && !selectedEntity) {
      toast.error('Please select a delegate or sponsor');
      return;
    }

    if (manualEntry && !manualName.trim()) {
      toast.error('Please enter a name for the manual entry');
      return;
    }

    const total = parseFloat(planForm.total_amount);
    const initial = parseFloat(planForm.initial_payment);
    const remaining = total - initial;
    const installmentAmount = remaining / planForm.installment_count;

    // Generate installment schedule
    const installments = [];
    const today = new Date();
    for (let i = 0; i < planForm.installment_count; i++) {
      const dueDate = new Date(today);
      dueDate.setDate(dueDate.getDate() + (30 * (i + 1))); // Monthly installments
      installments.push({
        amount: Math.round(installmentAmount * 100) / 100,
        due_date: dueDate.toISOString().split('T')[0],
        description: `Installment ${i + 1} of ${planForm.installment_count}`
      });
    }

    createPlanMutation.mutate({
      entity_type: manualEntry ? 'manual' : entityType,
      entity_id: manualEntry ? `manual_${Date.now()}` : selectedEntity,
      entity_name: manualEntry ? manualName : undefined,
      total_amount: total,
      initial_payment: initial,
      installments,
      processing_fee_type: planForm.processing_fee_type,
      processing_fee_amount: parseFloat(planForm.processing_fee_amount) || 0,
      notes: planForm.notes
    });
  };

  const handleRecordPayment = (e) => {
    e.preventDefault();
    if (!selectedPlan) return;

    recordPaymentMutation.mutate({
      planId: selectedPlan.id,
      data: {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference: paymentForm.reference,
        notes: paymentForm.notes
      }
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      defaulted: 'bg-red-100 text-red-700',
      cancelled: 'bg-slate-100 text-slate-700'
    };
    return <Badge className={styles[status] || styles.active}>{status}</Badge>;
  };

  // Calculate stats
  const stats = {
    total: plans.length,
    active: plans.filter(p => p.status === 'active').length,
    completed: plans.filter(p => p.status === 'completed').length,
    totalOwed: plans.filter(p => p.status === 'active').reduce((sum, p) => sum + (p.balance || 0), 0),
    totalCollected: plans.reduce((sum, p) => sum + (p.amount_paid || 0), 0)
  };

  if (loadingPlans) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Payment Plans</h2>
          <p className="text-slate-500">Manage installment payments and partial payments</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-plan-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Payment Plan
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-full">
                <Receipt className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Plans</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertCircle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Outstanding</p>
                <p className="text-2xl font-bold">${stats.totalOwed.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Collected</p>
                <p className="text-2xl font-bold">${stats.totalCollected.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="defaulted">Defaulted</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Plans List */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Plans</CardTitle>
        </CardHeader>
        <CardContent>
          {plans.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No payment plans created yet</p>
              <p className="text-sm">Create payment plans for delegates who want to pay in installments</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Delegate</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => {
                  const progress = ((plan.amount_paid || 0) / (plan.grand_total || plan.total_amount || 1)) * 100;
                  return (
                    <TableRow key={plan.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {plan.entity_type === 'sponsor' ? (
                            <TrendingUp className="h-4 w-4 text-purple-500" />
                          ) : plan.entity_type === 'manual' ? (
                            <Plus className="h-4 w-4 text-slate-400" />
                          ) : (
                            <User className="h-4 w-4 text-blue-500" />
                          )}
                          <div>
                            <span className="font-medium">{plan.entity_name || plan.delegate_name}</span>
                            <Badge variant="outline" className="ml-2 text-xs">
                              {plan.entity_type === 'sponsor' ? 'Sponsor' : plan.entity_type === 'manual' ? 'Manual' : 'Delegate'}
                            </Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        ${(plan.grand_total || plan.total_amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-green-600 font-medium">
                        ${(plan.amount_paid || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className={plan.balance > 0 ? 'text-amber-600 font-medium' : 'text-green-600'}>
                        ${(plan.balance || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 w-32">
                          <Progress value={progress} className="h-2" />
                          <span className="text-xs text-slate-500">{Math.round(progress)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(plan.status)}</TableCell>
                      <TableCell className="text-right">
                        {plan.status === 'active' && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedPlan(plan);
                              setPaymentForm({ ...paymentForm, amount: '' });
                              setShowPaymentDialog(true);
                            }}
                          >
                            <DollarSign className="h-4 w-4 mr-1" />
                            Record Payment
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Plan Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetPlanForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Payment Plan</DialogTitle>
            <DialogDescription>Set up an installment plan for a delegate or sponsor</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreatePlan} className="space-y-4">
            {/* Entity Type Selection */}
            <div className="space-y-3">
              <Label>Plan For *</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={entityType === 'delegate' && !manualEntry ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setEntityType('delegate'); setManualEntry(false); setSelectedEntity(null); }}
                >
                  <User className="h-4 w-4 mr-1" />
                  Delegate
                </Button>
                <Button
                  type="button"
                  variant={entityType === 'sponsor' && !manualEntry ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setEntityType('sponsor'); setManualEntry(false); setSelectedEntity(null); }}
                >
                  <TrendingUp className="h-4 w-4 mr-1" />
                  Sponsor
                </Button>
                <Button
                  type="button"
                  variant={manualEntry ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setManualEntry(true); setSelectedEntity(null); }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Manual Entry
                </Button>
              </div>
            </div>

            {/* Manual Entry Name */}
            {manualEntry ? (
              <div>
                <Label>Name *</Label>
                <Input
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder="Enter person or company name"
                  required
                />
              </div>
            ) : entityType === 'delegate' ? (
              <div>
                <Label>Select Delegate *</Label>
                <Select value={selectedEntity || ''} onValueChange={setSelectedEntity}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a delegate..." />
                  </SelectTrigger>
                  <SelectContent>
                    {delegates.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name || d.email} {d.company && `(${d.company})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Select Sponsor *</Label>
                <Select value={selectedEntity || ''} onValueChange={setSelectedEntity}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a sponsor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sponsors.length === 0 ? (
                      <SelectItem value="_none" disabled>No sponsors found</SelectItem>
                    ) : (
                      sponsors.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} ({s.tier || 'No tier'})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={planForm.total_amount}
                    onChange={(e) => setPlanForm({ ...planForm, total_amount: e.target.value })}
                    className="pl-9"
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Initial Payment (Deposit) *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={planForm.initial_payment}
                    onChange={(e) => setPlanForm({ ...planForm, initial_payment: e.target.value })}
                    className="pl-9"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>Number of Installments</Label>
              <Select 
                value={String(planForm.installment_count)} 
                onValueChange={(v) => setPlanForm({ ...planForm, installment_count: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 installment (single payment)</SelectItem>
                  <SelectItem value="2">2 installments</SelectItem>
                  <SelectItem value="3">3 installments</SelectItem>
                  <SelectItem value="4">4 installments</SelectItem>
                  <SelectItem value="6">6 installments</SelectItem>
                  <SelectItem value="12">12 installments</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Processing Fee Type</Label>
                <Select 
                  value={planForm.processing_fee_type} 
                  onValueChange={(v) => setPlanForm({ ...planForm, processing_fee_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Fee</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                    <SelectItem value="percentage">Percentage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {planForm.processing_fee_type !== 'none' && (
                <div>
                  <Label>{planForm.processing_fee_type === 'fixed' ? 'Fee Amount' : 'Fee Percentage'}</Label>
                  <div className="relative">
                    {planForm.processing_fee_type === 'fixed' ? (
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    ) : (
                      <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    )}
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={planForm.processing_fee_amount}
                      onChange={(e) => setPlanForm({ ...planForm, processing_fee_amount: e.target.value })}
                      className="pl-9"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Summary */}
            {planForm.total_amount && planForm.initial_payment && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-2">Plan Summary</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Total Amount:</span>
                    <span>${parseFloat(planForm.total_amount || 0).toFixed(2)}</span>
                  </div>
                  {planForm.processing_fee_type !== 'none' && (
                    <div className="flex justify-between">
                      <span>Processing Fee:</span>
                      <span>
                        ${planForm.processing_fee_type === 'fixed' 
                          ? parseFloat(planForm.processing_fee_amount || 0).toFixed(2)
                          : (parseFloat(planForm.total_amount || 0) * (parseFloat(planForm.processing_fee_amount || 0) / 100)).toFixed(2)
                        }
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium border-t pt-1">
                    <span>Grand Total:</span>
                    <span>
                      ${(parseFloat(planForm.total_amount || 0) + 
                        (planForm.processing_fee_type === 'fixed' 
                          ? parseFloat(planForm.processing_fee_amount || 0)
                          : parseFloat(planForm.total_amount || 0) * (parseFloat(planForm.processing_fee_amount || 0) / 100)
                        )).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Initial Payment:</span>
                    <span>-${parseFloat(planForm.initial_payment || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-amber-600 font-medium">
                    <span>Remaining ({planForm.installment_count} installments):</span>
                    <span>
                      ${(parseFloat(planForm.total_amount || 0) + 
                        (planForm.processing_fee_type === 'fixed' 
                          ? parseFloat(planForm.processing_fee_amount || 0)
                          : parseFloat(planForm.total_amount || 0) * (parseFloat(planForm.processing_fee_amount || 0) / 100)
                        ) - parseFloat(planForm.initial_payment || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createPlanMutation.isPending}>
                {createPlanMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Plan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            {selectedPlan && (
              <DialogDescription>
                Recording payment for {selectedPlan.entity_name || selectedPlan.delegate_name}. 
                Outstanding balance: ${selectedPlan.balance?.toFixed(2)}
              </DialogDescription>
            )}
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div>
              <Label>Payment Amount *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  max={selectedPlan?.balance || 99999}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="pl-9"
                  placeholder={`Max: ${selectedPlan?.balance?.toFixed(2) || '0.00'}`}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Payment Method</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reference/Receipt Number</Label>
              <Input
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                placeholder="Transaction reference..."
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="Optional notes..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={recordPaymentMutation.isPending}>
                {recordPaymentMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentPlanManager;
